/* istanbul ignore file */
import { useContext, useState, useEffect, RefObject } from "react";
import { useFlags } from "launchdarkly-react-client-sdk";
import { AppContext } from "../app/store";
import {
  Plan,
  SearchResultPlan,
  DrugCostInfo,
  LowIncomeSubsidyStatus,
  PrescriptionFrequency,
} from "../@types";
import { getDrugCosts } from "../api";
import { useTranslate } from "./intlHooks";
import { logError } from "../helpers";
import { getFullPlanId } from "./objectUtilities";
import { isSearchResultPlan } from "../@types/guards";

export const useCosts = (
  plan: Plan | SearchResultPlan,
  ref?: RefObject<HTMLElement>
): DrugCostInfo => {
  const t = useTranslate();
  const { state } = useContext(AppContext);
  const { mctCurrentPlanYear } = useFlags();
  const [costInfo, setCostInfo] = useState<DrugCostInfo>({
    costs: [],
    lowest_mail_total: t("plan_card.calculating"),
    lowest_retail_total: t("plan_card.calculating"),
    plan: getFullPlanId(plan),
    excluded_drugs: [],
    restrictions: [],
  });

  const updateCosts = (costs: DrugCostInfo): void => {
    setCostInfo(costs);
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    observer.disconnect();
  };

  const fetchCosts = (): void => {
    const prescriptions = state.prescriptions.map(p => ({
      ndc: p.ndc,
      frequency: p.frequency as PrescriptionFrequency,
      quantity: String(p.quantity), // FIXME: Need to update this to use the PDE quantity data if isHidden is true
    }));

    const npis = state.pharmacies.map(p => p.npi);
    const planId = getFullPlanId(plan);
    let lis;
    if (plan.contract_year === mctCurrentPlanYear.toString()) {
      lis = state.lis ? state.lis : LowIncomeSubsidyStatus.LIS_NO_HELP;
    } else {
      lis = state.futureLis
        ? state.futureLis
        : state.lis // anon users do not use futureLis
        ? state.lis
        : LowIncomeSubsidyStatus.LIS_NO_HELP;
    }

    getDrugCosts(npis, prescriptions, [planId], lis).then(
      res => (res ? updateCosts(res.plans[0]) : null),
      e => {
        logError("Could not get drug costs (costHooks/fetchCosts)", e);
      }
    );
  };

  const observer = new IntersectionObserver(
    ([entry]: IntersectionObserverEntry[]): void => {
      if (entry.isIntersecting) {
        fetchCosts();
      }
    }
  );

  useEffect(() => {
    if (!mctCurrentPlanYear) {
      return;
    }
    if (isSearchResultPlan(plan) && plan.remaining_premium_and_drugs) {
      return;
    }

    if (ref) {
      if (ref.current) {
        observer.observe(ref.current);
      }
      return (): void => observer.disconnect();
    } else {
      fetchCosts();
      return;
    }
  }, [plan, mctCurrentPlanYear]);

  return costInfo;
};
